class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

// ===========================================
// CUSTOM: Expanded filter drawer + dual price slider
// Dawn 15.4.1
// ===========================================
document.addEventListener('DOMContentLoaded', function () {
  document.addEventListener('click', function (event) {
    // Triggered when user clicks existing "Filter" button in toolbar
    var openTrigger = event.target.closest('.mobile-facets__open');
    if (!openTrigger) return;

    var mobileFacets = document.querySelector('.mobile-facets');
    if (!mobileFacets) return;

    // 1) Mark drawer as expanded for CSS and open all filter sections
    mobileFacets.classList.add('mobile-facets--expanded');

    mobileFacets.querySelectorAll('.mobile-facets__details').forEach(function (detailsEl) {
      detailsEl.setAttribute('open', 'open');
    });

    // 2) Initialize price slider once per drawer open
    var priceRange = mobileFacets.querySelector('price-range.facets__price');
    if (!priceRange || priceRange.dataset.sliderInitialized === 'true') return;
    priceRange.dataset.sliderInitialized = 'true';

    // Dawn provides two numeric inputs inside <price-range>
    var inputs = priceRange.querySelectorAll('input');
    if (inputs.length < 2) return;
    var minInput = inputs[0];
    var maxInput = inputs[1];

    // Helper to parse money like "₹6,999.00" -> 6999
    function parseMoney(value) {
      var cleaned = String(value)
        .replace(/[^\d.,]/g, '')   // keep only digits, comma, dot
        .replace(/,/g, '');        // remove thousand separators
      var num = parseFloat(cleaned);
      if (!isFinite(num)) num = 0;
      return Math.round(num);
    }

    var rawMin =
      minInput.getAttribute('data-min') ||
      minInput.getAttribute('min') ||
      minInput.value ||
      '0';

    var rawMax =
      maxInput.getAttribute('data-max') ||
      maxInput.getAttribute('max') ||
      maxInput.placeholder ||
      maxInput.value ||
      '0';

    var minNumeric = parseMoney(rawMin);
    var maxNumeric = parseMoney(rawMax);

    if (maxNumeric <= 0) maxNumeric = 10000;
    if (minNumeric < 0) minNumeric = 0;
    if (minNumeric > maxNumeric) minNumeric = 0;

    // Build slider UI
    var slider = document.createElement('div');
    slider.className = 'price-slider';
    slider.style.cssText = 'position:relative;width:100%;height:54px;padding-top:22px;';

    var labels = document.createElement('div');
    labels.className = 'price-slider__labels';
    labels.style.cssText = 'position:absolute;top:0;left:0;right:0;display:flex;justify-content:space-between;font-size:1.3rem;color:rgba(0,0,0,.6);';

    var minLabel = document.createElement('span');
    minLabel.className = 'price-slider__label price-slider__label--min';

    var maxLabel = document.createElement('span');
    maxLabel.className = 'price-slider__label price-slider__label--max';

    labels.appendChild(minLabel);
    labels.appendChild(maxLabel);

  // Track + selected range bars (your CSS already styles these)
    var track = document.createElement('div');
    track.className = 'price-slider__track';
    track.style.cssText = 'position:absolute;left:0;right:0;top:36px;height:3px;border-radius:999px;background:#e0e0e0;z-index:1;display:block;opacity:1;';

    var activeRange = document.createElement('div');
    activeRange.className = 'price-slider__range';
    activeRange.style.cssText = 'position:absolute;left:0;top:35px;height:6px;border-radius:999px;background:#8D2A2B;z-index:2;display:block;opacity:1;width:0;';

    // Put them inside slider BEFORE the inputs so thumbs sit above
    slider.appendChild(track);
    slider.appendChild(activeRange);

    var inputsWrapper = document.createElement('div');
    inputsWrapper.className = 'price-slider__inputs';
    inputsWrapper.style.cssText = 'position:absolute;left:0;right:0;top:36px;height:24px;z-index:3;';

    // Left handle: minimum
    var rangeMin = document.createElement('input');
    rangeMin.type = 'range';
    rangeMin.className = 'price-slider__input price-slider__input--min';
    rangeMin.min = '0';
    rangeMin.max = String(maxNumeric);
    rangeMin.value = String(minNumeric);

    // Right handle: maximum
    var rangeMax = document.createElement('input');
    rangeMax.type = 'range';
    rangeMax.className = 'price-slider__input price-slider__input--max';
    rangeMax.min = '0';
    rangeMax.max = String(maxNumeric);
    rangeMax.value = String(maxNumeric);

    inputsWrapper.appendChild(rangeMin);
    inputsWrapper.appendChild(rangeMax);

    slider.appendChild(labels);
    slider.appendChild(inputsWrapper);

    // Append slider to price-range
    priceRange.appendChild(slider);

    function formatINR(value) {
      return '₹' + Number(value).toLocaleString('en-IN');
    }

    function syncFromSlider(changed) {
      var minVal = parseInt(rangeMin.value, 10);
      var maxVal = parseInt(rangeMax.value, 10);

      if (minVal < 0) minVal = 0;
      if (maxVal > maxNumeric) maxVal = maxNumeric;

      // Prevent handles from crossing
      if (minVal > maxVal) {
        if (changed === 'min') {
          rangeMax.value = String(minVal);
          maxVal = minVal;
        } else if (changed === 'max') {
          rangeMin.value = String(maxVal);
          minVal = maxVal;
        }
      }
      // Update the visual active range bar (thick brand segment)
      var minPct = (minVal / maxNumeric) * 100;
      var maxPct = (maxVal / maxNumeric) * 100;

      activeRange.style.left = minPct + '%';
      activeRange.style.width = (maxPct - minPct) + '%';

      // Update hidden Dawn inputs
      minInput.value = String(minVal);
      maxInput.value = String(maxVal);

      // Update visible labels
      minLabel.textContent = formatINR(minVal);
      maxLabel.textContent = formatINR(maxVal);

      // Trigger change so Dawn's filtering runs
      var changeEvent;
      try {
        changeEvent = new Event('change', { bubbles: true });
      } catch (e) {
        changeEvent = document.createEvent('Event');
        changeEvent.initEvent('change', true, true);
      }
      maxInput.dispatchEvent(changeEvent);
    }

    // Initial values
    syncFromSlider();

    rangeMin.addEventListener('input', function () {
      syncFromSlider('min');
    });
    rangeMax.addEventListener('input', function () {
      syncFromSlider('max');
    });
    rangeMin.addEventListener('change', function () {
      syncFromSlider('min');
    });
    rangeMax.addEventListener('change', function () {
      syncFromSlider('max');
    });
  });
});